/* eslint-disable no-unused-vars */
import React from 'react';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, Menu, MenuItem, useTheme,  } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { EventTypes, IMatchupsResponse, ITeam, Leagues } from './store/types';
import { getLeague } from './optimizerHelper';

interface OwnProps {
  readonly league?: Leagues;
  readonly matchups: IMatchupsResponse[];
  readonly filters: string[];
  readonly AddMatchupFilter: (matchup: IMatchupsResponse) => void;
  readonly RemoveMatchupFilter: (matchup: IMatchupsResponse) => void;
}

type Props = OwnProps;

const Component = (props: Props) => {
  const {
    league, matchups, filters, AddMatchupFilter, RemoveMatchupFilter,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const eventType = matchups?.length ? matchups[0]?.eventType : EventTypes.Game;
  const showTournament = eventType === EventTypes.Tournament || league === Leagues.GOLF;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getDisplayLabel = (matchup: IMatchupsResponse) => {
    const homeTeam = matchup?.participants?.find((p) => p.isHome)?.team || ({} as ITeam);
    const awayTeam = matchup?.participants?.find((p) => !p.isHome)?.team || ({} as ITeam);
    let displayLabel = <div />;
    let isCollege =
      [Leagues.NCAAF, Leagues.NCAAB].indexOf(
        getLeague(matchup?.leagueInstance?.leagueCode),
      ) > -1;

    if (matchup.eventType === EventTypes.Tournament) {
      displayLabel = (
        <Box className="row">
          <Box className="tournament">
            <Box
              component="div"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {matchup?.name}
            </Box>
          </Box>
        </Box>
      );
    } else {
      displayLabel = (
        <Box className="row">
          <Box className="team">
            <Box className="img-wrapper">
              <img src={awayTeam?.logo} alt="" />
            </Box>
            <Box
              component="div"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {(isCollege ? awayTeam.school : awayTeam.title)}
            </Box>
          </Box>
          <span>@</span>
          <Box className="team">
            <Box className="img-wrapper">
              <img src={homeTeam?.logo} alt=""/>
            </Box>
            <Box
              component="div"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {(isCollege ? homeTeam.school : homeTeam.title)}
            </Box>
          </Box>
        </Box>
      );
    }

    return displayLabel;
  };

  const handleChange = (event: any, option: IMatchupsResponse) => {
    const toAdd = event.target.checked;
    if (toAdd) {
      AddMatchupFilter(option);
    } else if (!toAdd) {
      RemoveMatchupFilter(option);
    }
  };
  const theme = useTheme();
  return (
    <Grid
      sx={{
        root: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          [theme.breakpoints.down('xs')]: {
            justifyContent: 'center',
          },
        }
      }}
      item>
      <Box
        sx={{
          root: {
            width: '100%',
          }
        }}
        data-filter="matchups">
        <Button
          sx={{
            backgroundColor: `#4395ff !important`,
            border: '0px !important',
            height: 40,
            minWidth: 125,
            width: '100%',
            maxWidth: '100% !important',
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
            justifyContent: 'space-between !important',
            textTransform: 'capitalize',
            padding: '4px 12px !important',
            color: theme.palette.common.white,
            fontWeight: 500,
            fontSize: 13,
            '&.MuiButton-contained': {
              color: theme.palette.common.white,
              backgroundColor: `#1C1C1C !important`,
              border: '0px !important',
              textTransform: 'capitalize',
            },
            '& .MuiSvgIcon-root': {
              color: theme.palette.common.white,
            },
            '&.selected': {
              backgroundColor: theme.palette.grey[700],
              color: theme.palette.getContrastText(theme.palette.grey[700]),
              fontWeight: 500,
            },
            '&.disabled': {
              backgroundColor: theme.palette.grey[100],
              color: theme.palette.getContrastText(theme.palette.grey[100]),
              fontWeight: 500,
            },
          }}
          aria-controls="button-menu"
          aria-haspopup="true"
          variant="contained"
          color="primary"
          onClick={handleClick}
          disabled={league === Leagues.ALL}
          endIcon={<ArrowDropDownIcon color="secondary" />}
        >
          {showTournament ? 'Tournaments' : 'Matchups'} {filters?.length > 0 ? `(${filters?.length})` : ''}
        </Button>
        <Menu
          sx={{
            [theme.breakpoints.up('md')]: {
              width: '425px',
            },
            '& .MuiPaper-root': {
              border: '1px solid #d3d4d5',
              '& .row': {
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'row',
                alignItems: 'center',
                '& .team': {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  minWidth: 150,
                  width: 120,
                  [theme.breakpoints.down('sm')]: {
                    maxWidth: '125px !important',
                    minWidth: '125px !important',
                    width: '125px !important',
                  },
                  whiteSpace: 'nowrap',
                  '& .img-wrapper': {
                    width: '20px',
                    height: '20px',
                    marginRight: '4px',
                    '& img': {
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                    },
                  },
                  '& p': {
                    fontWeight: 500,
                  },
                },
                '& .tournament': {
                  minWidth: 150,
                  width: 300,
                },
                '& span': {
                  marginRight: '4px',
                  marginLeft: '8px',
                },
              },
            },
          }}
          elevation={0}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            elevation: 1,
            style: {
              maxHeight: 100 * 4.5,
              width: '55ch',
            },
          }}
          // {...props}
          id="customized-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {matchups?.length === 0
            && <MenuItem
              disabled={true}>
              No {showTournament ? 'Tournaments' : 'Matchups'} Found
            </MenuItem>}
          {matchups?.length > 0
            && matchups?.map((option: IMatchupsResponse) => (
              <MenuItem key={option.id} sx={{ width: '100%' }}>
                <FormControl
                  key={option.id} id={option.id} sx={{ width: '100%' }}>
                  <FormGroup>
                    <FormControlLabel
                      sx={{ display: 'flex', justifyContent: 'space-between', marginRight: '16px' }}
                      key={option.id}
                      id={option.id}
                      value="start"
                      control={
                        <Checkbox
                          checked={
                            !!filters?.find((fd) => fd === option.id)
                          }
                          size="small"
                          color="secondary"
                          onChange={(e) => handleChange(e, option)}
                        />
                      }
                      label={getDisplayLabel(option)}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </FormControl>
              </MenuItem>
            ))}
        </Menu>
      </Box>
    </Grid>
  );
};

export default Component;
